/* eslint-disable no-param-reassign */
import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import { bool, func, shape } from 'prop-types';
import { shallow } from 'zustand/shallow';
import clsx from 'clsx';
import { truncate } from 'lodash';
import { gsap } from 'gsap';

import { text } from 'polyglot/polyglot';
import api from 'utils/api';
import { parseBingoCardsLayouts, validateTicketFormat } from 'utils/helpers';
import { formatCurrency } from 'utils/currency';

import useBreakpoint, {
  DESKTOP,
  TABLET_LANDSCAPE,
  MOBILE,
} from 'hooks/useBreakpoint';
import useGameStore, { ACTIVE_GAME, PURCHASE_VIEW } from 'stores/game';
import { useChatStore } from 'stores/chat';
import usePurchaseStore from 'stores/purchase';
import useUserStore from 'stores/user';
import useCurrentGameStore from 'stores/current-game';
import useUpcomingGameStore from 'stores/upcoming-game';
import useApiErrorStore from 'stores/api-error';
import useLayoutStore from 'stores/layout';
import tutorialStore from 'stores/tutorial';
import bingoAssetsStore from 'stores/bingoAssetsStore';

import UpcomingGameBanner from 'components/UpcomingGameBanner/UpcomingGameBanner';
import Button from 'components/Button/Button';
import GameWallet from 'components/GameWallet/GameWallet';
import ListOfPrizes from 'components/GameInfoPanel/ListOfPrizes/ListOfPrizes';

import ChatIcon from 'themes/common/svg/chat-icon.svg';
import Cross from 'themes/common/svg/cross.svg';

import BuyLotteryTicketButtons from './BuyLotteryTicketButtons/BuyLotteryTicketButtons';
import BonusNotification from '../BonusNotification/BonusNotification';

import styles from './PurchaseLayout.module.scss';

const chatIconPositionSelector = (state) => state.setChatIconPosition;
const hasNewMessagesSelector = (state) => state.hasNewMessages;

const setIsChatExpandedSelector = (state) => state.setIsChatExpanded;
const gameStateSelector = (state) => state.gameState;

const isLoadingSelector = (state) => [state.isLoading, state.setIsLoading];
const bingoCardsListSelector = (state) => [
  state.bingoCardsList,
  state.setBingoCardsList,
];
const userSelector = (state) => [state.user];
const selectedCardIdsSelector = (state) => [
  state.selectedCardIds,
  state.resetSelectedCardIds,
];
const currentGameSelector = (state) => state.currentGame;
const prebuyTicketAmountSelector = (state) => [
  state.prebuyTicketAmount,
  state.updatePrebuyTicketAmount,
];
const upcomingGameSelector = (state) => state.nextGame;
const apiErrorSelector = (state) => state.setErrorMessage;
const isSmallTicketsSelector = (state) => [
  state.isSmallTickets,
  state.setIsSmallTickets,
];
const isLongTicketsSelector = (state) => [
  state.isLongTickets,
  state.setIsLongTickets,
];
const setIs4x4TicketsSelector = (state) => state.setIs4x4Tickets;
const numberOfTicketsCurrentGameSelector = (state) =>
  state.updateTicketsCurrentGame;

const fetchNewCardsSelector = (state) => [
  state.fetchNewCards,
  state.setFetchNewCards,
];

const prePurchaseGameListSelector = (state) => [
  state.prePurchaseGameList,
  state.setPrePurchaseGameList,
];
const setIsPrePurchaseExpandedSelector = (state) => [
  state.isPrePurchaseExpanded,
  state.setIsPrePurchaseExpanded,
];

const fetchGameAssetsSelector = (state) => state.fetchGameAssets;
function PurchaseLotteryTicketsPanel({
  setIsPurchaseDuringGame,
  isPurchaseDuringGame,
  setHasPurchaseModal,
  displayBonus,
}) {
  const mountedRef = useRef(true);
  const tabRefs = useRef([]);

  const desktopAccordionRefs = useRef({});
  const mobileContentRef = useRef();
  const bannerWrapperRef = useRef();
  const purchaseButtonsRef = useRef();

  const [expandedIndex, setExpandedIndex] = useState(0);
  const [previousExpandedIndex, setPreviousExpandedIndex] = useState(0);

  const setChatIconPosition = tutorialStore(chatIconPositionSelector);
  const breakpoint = useBreakpoint();
  const isDesktop = breakpoint === DESKTOP;
  const isLandscape = breakpoint === TABLET_LANDSCAPE;
  const isMobile = breakpoint === MOBILE;
  const chatReference = useRef(0);

  const setIsChatExpanded = useChatStore(setIsChatExpandedSelector);
  const hasNewMessages = useChatStore(hasNewMessagesSelector);

  const [isLoading, setIsLoading] = usePurchaseStore(
    isLoadingSelector,
    shallow
  );
  const [bingoCardsList, setBingoCardsList] = usePurchaseStore(
    bingoCardsListSelector,
    shallow
  );
  const [user] = useUserStore(userSelector);
  const [selectedTicketIds, resetSelectedCardIds] = usePurchaseStore(
    selectedCardIdsSelector,
    shallow
  );
  const currentGame = useCurrentGameStore(currentGameSelector);
  const nextGame = useUpcomingGameStore(upcomingGameSelector);
  const [prebuyTicketAmount, updatePrebuyTicketAmount] = usePurchaseStore(
    prebuyTicketAmountSelector
  );
  const setApiErrorMessage = useApiErrorStore(apiErrorSelector);
  const [isSmallTickets, setIsSmallTickets] = useLayoutStore(
    isSmallTicketsSelector,
    shallow
  );
  const [isLongTickets, setIsLongTickets] = useLayoutStore(
    isLongTicketsSelector,
    shallow
  );
  const setIs4x4Tickets = useLayoutStore(setIs4x4TicketsSelector);
  const updateTicketsCurrentGame = usePurchaseStore(
    numberOfTicketsCurrentGameSelector
  );
  const gameState = useGameStore(gameStateSelector);
  const [fetchNewCards, setFetchNewCards] = usePurchaseStore(
    fetchNewCardsSelector,
    shallow
  );

  const [prePurchaseGameList, setPrePurchaseGameList] = useUpcomingGameStore(
    prePurchaseGameListSelector,
    shallow
  );

  const [isPrePurchaseExpanded, setIsPrePurchaseExpanded] = usePurchaseStore(
    setIsPrePurchaseExpandedSelector
  );

  const fetchGameAssets = bingoAssetsStore(fetchGameAssetsSelector);

  const onClickCloseButton = () => {
    updatePrebuyTicketAmount(0);
    setIsPurchaseDuringGame(false);

    if (!isDesktop && isLandscape) {
      setIsPrePurchaseExpanded(false);
    }
    if (isMobile) {
      setIsPrePurchaseExpanded(false);
    }
  };

  const handleAccordionChange = (idx) => {
    setPreviousExpandedIndex(expandedIndex);
    updatePrebuyTicketAmount(0);
    setExpandedIndex(idx);

    const tab = tabRefs.current[idx];
    const container = tab?.parentElement;

    if (!isDesktop && tab && container) {
      const tabRect = tab.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const offset =
        tabRect.left -
        containerRect.left -
        containerRect.width / 2 +
        tabRect.width / 2;

      const maxScrollLeft = container.scrollWidth - containerRect.width;
      let targetScrollLeft = container.scrollLeft + offset;
      if (targetScrollLeft < 0) targetScrollLeft = 0;
      if (targetScrollLeft > maxScrollLeft) targetScrollLeft = maxScrollLeft;

      container.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
    }

    if (!isDesktop && isLandscape) {
      setIsPrePurchaseExpanded(true);
    }
  };

  const bingoRoundIdRef = useRef();
  const isPurchaseView = gameState === PURCHASE_VIEW;

  const onClickBuy = () => {
    if (!user.isAuthenticated) {
      if (user.callback_login && typeof user.callback_login === 'function') {
        user.callback_login();
      }
      return;
    }
    setHasPurchaseModal(true);
  };

  const checkIfTicketIsSmall = (ticket) => {
    if (ticket.length === 5 && !isSmallTickets) {
      setIsSmallTickets(true);
    }
    if (ticket.length !== 5 && isSmallTickets) {
      setIsSmallTickets(false);
    }
  };

  const checkIfTicketIsLong = (ticketRow) => {
    if (ticketRow.length === 9 && !isLongTickets) {
      setIsLongTickets(true);
    }
    if (ticketRow.length < 9 && isLongTickets) {
      setIsLongTickets(false);
    }
  };

  const checkIfTicketIs4x4 = (ticket) => {
    const is4x4 = validateTicketFormat(ticket, 4, 4);
    setIs4x4Tickets(is4x4);
  };

  const checkTicketLayout = (ticket) => {
    checkIfTicketIsSmall(ticket.numbersMap);
    checkIfTicketIsLong(ticket.numbersMap[0]);
    checkIfTicketIs4x4(ticket);
  };

  const loadBingoCards = async () => {
    if (gameState === ACTIVE_GAME) return;
    if (
      !bingoCardsList.length ||
      currentGame.bingoRoundId !== bingoRoundIdRef.current
    ) {
      if (!isLoading && currentGame.bingoRoundId) {
        setIsLoading(true);
        if (bingoCardsList.length) setBingoCardsList([]);
        await api
          .get(`/api/Bingo/cards/${currentGame.bingoRoundId}`)
          .then((res) => {
            setIsLoading(false);
            if (mountedRef.current && res.length) {
              bingoRoundIdRef.current = currentGame.bingoRoundId;
              let purchasedCards = 0;
              const bingoCards = res.map((item, index) => {
                parseBingoCardsLayouts(item);
                if (item.bought) {
                  purchasedCards += 1;
                }
                item.canBePurchased = !item.bought;
                item.isPurchased = item.bought;
                item.id = item.checksum || item.cardId;
                item.cardNumber = String(index + 1);
                return item;
              });
              checkTicketLayout(res[0]);
              updateTicketsCurrentGame(purchasedCards);
              const maxNumberOfCardsReached =
                purchasedCards === currentGame.maxNumberOfCards;
              if (maxNumberOfCardsReached) {
                setBingoCardsList(
                  bingoCards.filter((ticket) => ticket.isPurchased)
                );
              } else {
                setBingoCardsList(bingoCards);
              }
              resetSelectedCardIds();
            }
          })
          .catch((err) => {
            if (mountedRef.current) {
              console.log('error on retrieving tickets: ', err);
              setApiErrorMessage(5);
              setIsLoading(false);
            }
          });
      }
    }
  };

  // Fetch upcoming rounds when purchase during game to update prebuy list
  useEffect(() => {
    if (isPurchaseDuringGame && user.room) {
      setIsLoading(true);
      api
        .get(`/api/Bingo/rooms/${user.room}/upcoming`)
        .then((response) => {
          setPrePurchaseGameList(response);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('Failed to fetch upcoming rounds:', error);
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPurchaseDuringGame, user.room]);

  useEffect(() => {
    mountedRef.current = true;
    if (mountedRef.current) {
      setTimeout(() => {
        loadBingoCards();
      }, Math.random() * 3000);
    }

    // Clean up.
    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGame]);

  useEffect(() => {
    if (fetchNewCards) {
      loadBingoCards();
      setFetchNewCards(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchNewCards]);

  function updatePosition() {
    if (!isDesktop && chatReference.current) {
      setChatIconPosition({
        chatReference: chatReference.current.getBoundingClientRect(),
      });
    }
  }

  useLayoutEffect(() => {
    window.addEventListener('resize', updatePosition);
    updatePosition();
    return () => window.removeEventListener('resize', updatePosition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop]);

  useEffect(() => {
    setTimeout(() => {
      updatePosition();
    }, 400);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPurchaseView, breakpoint]);

  // GSAP animation for desktop accordion
  useEffect(() => {
    if (isDesktop && prePurchaseGameList.length > 0) {
      const contentEl = desktopAccordionRefs.current[expandedIndex];
      if (contentEl) {
        gsap.set(contentEl, {
          height: 0,
          opacity: 0,
          overflow: 'hidden',
          transformOrigin: 'top center',
        });

        gsap.to(contentEl, {
          height: 'auto',
          opacity: 1,
          duration: 0.4,
          ease: 'power2.out',
          onComplete: () => {
            gsap.set(contentEl, { overflow: 'visible' });
          },
        });
      }
    }
  }, [expandedIndex, isDesktop, prePurchaseGameList.length]);

  // GSAP animation for mobile accordion content
  useEffect(() => {
    if (
      !isDesktop &&
      prePurchaseGameList.length > 1 &&
      mobileContentRef.current
    ) {
      const direction = expandedIndex > previousExpandedIndex ? 1 : -1;

      const tl = gsap.timeline();

      gsap.set(
        [
          mobileContentRef.current,
          bannerWrapperRef.current,
          purchaseButtonsRef.current,
        ],
        {
          clearProps: 'all',
        }
      );

      tl.fromTo(
        mobileContentRef.current,
        {
          opacity: 0,
          x: 80 * direction,
          transformOrigin: direction > 0 ? 'left center' : 'right center',
        },
        {
          opacity: 1,
          x: 0,
          duration: 0.45,
          ease: 'power2.out',
        }
      );

      if (bannerWrapperRef.current) {
        tl.fromTo(
          bannerWrapperRef.current,
          {
            opacity: 0,
            x: 60 * direction,
            transformOrigin: direction > 0 ? 'left center' : 'right center',
          },
          {
            opacity: 1,
            x: 0,
            duration: 0.4,
            ease: 'power2.out',
          },
          '<'
        );
      }

      if (purchaseButtonsRef.current) {
        tl.fromTo(
          purchaseButtonsRef.current,
          {
            opacity: 0,
            x: 65 * direction,
            transformOrigin: direction > 0 ? 'left center' : 'right center',
          },
          {
            opacity: 1,
            x: 0,
            duration: 0.4,
            ease: 'power2.out',
          },
          '<'
        );
      }
    }
  }, [
    expandedIndex,
    isDesktop,
    previousExpandedIndex,
    prePurchaseGameList.length,
  ]);

  useEffect(() => {
    if (prePurchaseGameList?.length) {
      prePurchaseGameList.forEach((round) => {
        if (round.bingoGame) {
          fetchGameAssets(round.bingoGame);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prePurchaseGameList]);

  // time formatting for draw start time in accordion header
  function formatTimeToStart(drawStartTime) {
    const now = new Date();
    const start = new Date(drawStartTime);
    const diffMs = start - now;

    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return text.t('prePurchaseCountdownTime.minutes', { minutes: diffMins });
    }
    if (diffHours < 24) {
      const mins = diffMins % 60;
      return text.t('prePurchaseCountdownTime.hours', {
        hours: diffHours,
        minutes: mins,
      });
    }
    const hours = diffHours % 24;
    const mins = diffMins % 60;
    return text.t('prePurchaseCountdownTime.days', {
      days: diffDays,
      hours,
      minutes: mins,
    });
  }

  // Accordion structure for desktop and horizontal tab row for mobile/tablet
  const renderAccordion = () => {
    if (isDesktop) {
      return (
        <div className={styles.accordionWrapper}>
          <div className={styles.accordionHeader}>
            <span className={styles.headerText}>
              {text.t('purchaseLayout.upcomingGames')}
            </span>
          </div>
          {prePurchaseGameList.map((round, idx) => {
            const isExpanded = expandedIndex === idx;
            const { bingoGame, drawStartTime, cardsAlreadyBought } = round;
            return (
              <div
                key={bingoGame.bingoRoundId}
                className={clsx(styles.accordionItem, {
                  [styles.expanded]: isExpanded,
                })}
              >
                <button
                  className={styles.accordionHeader}
                  type="button"
                  onClick={() => handleAccordionChange(idx)}
                  aria-expanded={isExpanded}
                >
                  <div className={styles.gameInfo}>
                    <div className={styles.headerTopRow}>
                      <span className={styles.gameName}>
                        {truncate(bingoGame.name, { length: 22 })}
                      </span>
                      <span className={styles.gameTime}>
                        {formatTimeToStart(drawStartTime)}
                      </span>
                    </div>
                    <div className={styles.headerBottomRow}>
                      <span className={styles.cardsBought}>
                        {text.t('purchaseLayout.cardsBought', {
                          count: cardsAlreadyBought,
                        })}
                      </span>
                      <span className={styles.cardPrice}>
                        {formatCurrency(bingoGame.cardPrice)}
                      </span>
                    </div>
                  </div>
                </button>

                <div
                  className={styles.accordionContent}
                  ref={(el) => {
                    desktopAccordionRefs.current[idx] = el;
                  }}
                  style={{
                    display: isExpanded ? 'block' : 'none',
                    overflow: 'hidden',
                  }}
                >
                  {isExpanded && (
                    <div className={styles.purchaseSection}>
                      <div className={styles.bannerWrapper}>
                        <UpcomingGameBanner
                          largeBanner
                          hideInfoButton={idx !== 0}
                          game={bingoGame}
                          timeToStart={drawStartTime}
                        />
                      </div>
                      <div className={styles.purchaseButtonsWrapper}>
                        <BuyLotteryTicketButtons
                          isPrebuy
                          cardsAlreadyBought={cardsAlreadyBought}
                          maxNumberOfCards={
                            bingoGame.maximumNumberOfCardsPerPlayer
                          }
                          bingoRoundId={bingoGame.bingoRoundId}
                        />
                        <Button
                          onClick={onClickBuy}
                          className={styles.payButton}
                          isDisabled={
                            !prebuyTicketAmount ||
                            cardsAlreadyBought >=
                              bingoGame.maximumNumberOfCardsPerPlayer
                          }
                        >
                          <span className={styles.bold}>
                            {text.t('buyLotteryTicketButtons.payButton')}
                          </span>
                          <span className={styles.unBold}>
                            {text.t('buyLotteryTicketButtons.totalAmount', {
                              amount: formatCurrency(
                                prebuyTicketAmount * bingoGame.cardPrice
                              ),
                            })}
                          </span>
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Mobile/Tablet: horizontal tab row
    return (
      <div className={styles.accordionWrapper}>
        <div className={styles.accordionTabRow}>
          {prePurchaseGameList.map((round, idx) => {
            const { bingoGame, drawStartTime, cardsAlreadyBought } = round;
            const isExpanded = expandedIndex === idx;
            return (
              <button
                key={bingoGame.bingoRoundId}
                className={clsx(styles.accordionTab, {
                  [styles.expanded]: isExpanded,
                })}
                type="button"
                ref={(el) => {
                  tabRefs.current[idx] = el;
                }}
                onClick={() => handleAccordionChange(idx)}
                aria-expanded={isExpanded}
              >
                <div className={styles.gameInfo}>
                  <div className={styles.headerTopRow}>
                    <span className={styles.gameName}>
                      {truncate(bingoGame.name, { length: 22 })}
                    </span>
                    <span>-</span>
                    <span className={styles.gameTime}>
                      {formatTimeToStart(drawStartTime)}
                    </span>
                  </div>
                  <div className={styles.headerBottomRow}>
                    <span className={styles.cardsBought}>
                      {text.t('purchaseLayout.cardsBought', {
                        count: cardsAlreadyBought,
                      })}
                    </span>
                    <span className={styles.cardPrice}>
                      {formatCurrency(bingoGame.cardPrice)}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div
          className={styles.accordionContentMobile}
          ref={mobileContentRef}
          key={expandedIndex}
        >
          {prePurchaseGameList.map((round, idx) => {
            if (expandedIndex !== idx) return null;
            const isExpanded = expandedIndex === idx;
            const { bingoGame, drawStartTime, cardsAlreadyBought } = round;
            return (
              <div
                className={styles.purchaseSection}
                key={bingoGame.bingoRoundId}
              >
                <div
                  className={clsx(styles.bannerWrapper, {
                    [styles.expanded]: isExpanded,
                    [styles.isLandscape]: isLandscape,
                  })}
                  ref={bannerWrapperRef}
                >
                  <UpcomingGameBanner
                    largeBanner
                    hideInfoButton={idx !== 0}
                    game={bingoGame}
                    timeToStart={drawStartTime}
                  />
                </div>
                <div
                  className={clsx(styles.purchaseButtonsWrapper, {
                    [styles.expanded]: isExpanded,
                    [styles.isLandscape]: isLandscape,
                  })}
                  ref={purchaseButtonsRef}
                >
                  <BuyLotteryTicketButtons
                    isPrebuy
                    cardsAlreadyBought={cardsAlreadyBought}
                    maxNumberOfCards={bingoGame.maximumNumberOfCardsPerPlayer}
                    bingoRoundId={bingoGame.bingoRoundId}
                    isMobileExpanded={isExpanded}
                  />
                  <Button
                    onClick={onClickBuy}
                    className={clsx(styles.payButton, {
                      [styles.expanded]: isExpanded,
                      [styles.isLandscape]: isLandscape,
                    })}
                    isDisabled={
                      !prebuyTicketAmount ||
                      cardsAlreadyBought >=
                        bingoGame.maximumNumberOfCardsPerPlayer
                    }
                  >
                    <span className={styles.bold}>
                      {text.t('buyLotteryTicketButtons.payButton')}
                    </span>
                    <span className={styles.unBold}>
                      {text.t('buyLotteryTicketButtons.totalAmount', {
                        amount: formatCurrency(
                          prebuyTicketAmount * bingoGame.cardPrice
                        ),
                      })}
                    </span>
                  </Button>
                </div>
                {user.isAuthenticated &&
                  displayBonus &&
                  breakpoint !== DESKTOP && (
                    <div
                      className={clsx(styles.bonus, {
                        [styles.isLandscape]: isLandscape,
                      })}
                    >
                      <BonusNotification bonus={displayBonus} />
                    </div>
                  )}
                {isLandscape && (
                  <div className={styles.smallBanner}>
                    <UpcomingGameBanner nakedBanner />
                  </div>
                )}
                {breakpoint !== DESKTOP && (
                  <div
                    className={clsx(styles.gameWalletAndChatWrapper, {
                      [styles.isNotLoggedIn]: !user.isAuthenticated,
                      [styles.expanded]: isExpanded,
                      [styles.isLandscape]: isLandscape,
                    })}
                  >
                    {user.isAuthenticated && (
                      <div className={styles.gameWallet}>
                        <GameWallet />
                      </div>
                    )}
                    <Button
                      isIcon
                      onClick={() => {
                        setIsChatExpanded(true);
                        useChatStore.setState({ hasNewMessages: false });
                      }}
                      className={styles.chatIconButton}
                    >
                      <div
                        className={styles.chatIconWrapper}
                        ref={chatReference}
                      >
                        <ChatIcon className={styles.chatIcon} />
                        {hasNewMessages && (
                          <span className={styles.newMessageIndicator}>!</span>
                        )}
                      </div>
                    </Button>
                  </div>
                )}
                {user.isAuthenticated &&
                  displayBonus &&
                  breakpoint === DESKTOP && (
                    <div className={styles.bonus}>
                      <BonusNotification bonus={displayBonus} />
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      {/* render new accordion layout */}
      {prePurchaseGameList.length > 1 && isPurchaseDuringGame ? (
        renderAccordion()
      ) : (
        <>
          {/* old style if no pre-purchase games are available */}
          <div
            className={clsx(styles.bannerWrapper, {
              [styles.isLandscape]: isLandscape,
            })}
          >
            <UpcomingGameBanner
              largeBanner
              hideInfoButton={isPurchaseDuringGame}
            />

            {breakpoint === DESKTOP && (
              <div className={styles.prizeWrapper}>
                <ListOfPrizes
                  purchaseView
                  displayNextGame={isPurchaseDuringGame}
                />
              </div>
            )}
          </div>
          <div
            className={clsx(styles.purchaseButtonsWrapper, {
              [styles.isLandscape]: isLandscape,
            })}
          >
            <div className={styles.ticketButtonsWrapper}>
              <BuyLotteryTicketButtons isPrebuy={isPurchaseDuringGame} />
            </div>
            <div
              className={clsx(styles.innerWrapper, {
                [styles.isLandscape]: isLandscape,
              })}
            >
              <Button
                onClick={onClickBuy}
                className={styles.payButton}
                isDisabled={
                  isPurchaseDuringGame
                    ? !prebuyTicketAmount || !nextGame.bingoGameId
                    : !selectedTicketIds.length || !currentGame.bingoGameId
                }
              >
                <span className={styles.bold}>
                  {text.t('buyLotteryTicketButtons.payButton')}
                </span>
                {isPurchaseDuringGame ? (
                  <span className={styles.unBold}>
                    {text.t('buyLotteryTicketButtons.totalAmount', {
                      amount: formatCurrency(
                        prebuyTicketAmount * nextGame.cardPrice
                      ),
                    })}
                  </span>
                ) : (
                  <span className={styles.unBold}>
                    {text.t('buyLotteryTicketButtons.totalAmount', {
                      amount: formatCurrency(
                        currentGame.cardPrice
                          ? currentGame.cardPrice * selectedTicketIds.length
                          : 0
                      ),
                    })}
                  </span>
                )}
              </Button>
              {user.isAuthenticated &&
                displayBonus &&
                breakpoint !== DESKTOP && (
                  <div
                    className={clsx(styles.bonus, {
                      [styles.isLandscape]: isLandscape,
                    })}
                  >
                    <BonusNotification bonus={displayBonus} />
                  </div>
                )}
              {isLandscape && (
                <div className={styles.smallBanner}>
                  <UpcomingGameBanner nakedBanner />
                </div>
              )}
              {breakpoint !== DESKTOP && (
                <div
                  className={clsx(styles.gameWalletAndChatWrapper, {
                    [styles.isNotLoggedIn]: !user.isAuthenticated,
                    [styles.isLandscape]: isLandscape,
                  })}
                >
                  {user.isAuthenticated && (
                    <div className={styles.gameWallet}>
                      <GameWallet />
                    </div>
                  )}
                  <Button
                    isIcon
                    onClick={() => {
                      setIsChatExpanded(true);
                      useChatStore.setState({ hasNewMessages: false });
                    }}
                    className={styles.chatIconButton}
                  >
                    <div className={styles.chatIconWrapper} ref={chatReference}>
                      <ChatIcon className={styles.chatIcon} />
                      {hasNewMessages && (
                        <span className={styles.newMessageIndicator}>!</span>
                      )}
                    </div>
                  </Button>
                </div>
              )}
            </div>
            {user.isAuthenticated && displayBonus && isDesktop && (
              <div className={styles.bonus}>
                <BonusNotification bonus={displayBonus} />
              </div>
            )}
          </div>
        </>
      )}
      {isPurchaseDuringGame && (
        <>
          <button
            type="button"
            className={clsx(styles.closeButton, {
              [styles.isLandscape]: isLandscape,
              [styles.isExpanded]: isPrePurchaseExpanded,
            })}
            onClick={onClickCloseButton}
          >
            <span
              className={clsx(styles.crossWrapper, {
                [styles.isUpcoming]:
                  !isDesktop && prePurchaseGameList.length > 1,
              })}
            >
              <Cross className={styles.crossIcon} />
            </span>
          </button>
          {!isDesktop && prePurchaseGameList.length > 1 && (
            <div
              className={clsx(styles.upcomingRoundsTitle, {
                [styles.isLandscape]: isLandscape,
              })}
            >
              {text.t('purchaseLayout.upcomingGames')}
            </div>
          )}
        </>
      )}
    </div>
  );
}

PurchaseLotteryTicketsPanel.propTypes = {
  setIsPurchaseDuringGame: func.isRequired,
  isPurchaseDuringGame: bool.isRequired,
  setHasPurchaseModal: func.isRequired,
  displayBonus: shape(),
};

PurchaseLotteryTicketsPanel.defaultProps = {
  displayBonus: null,
};

export default PurchaseLotteryTicketsPanel;
